/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'

import React from 'react'
import { View, Avatar } from '@components/atoms'
import Cropper, { ReactCropperElement } from 'react-cropper'
import 'cropperjs/dist/cropper.css'

import { useAppSelector, useAppDispatch, customerAuthActions } from '@redux/index'
import { CustomerType } from '@schemas'
import { mono, monoFont } from '../monolith.tokens'
import { checkPickedFile, ImageCheckResult } from './imageGuard'

const ACCEPT = 'image/jpeg,image/png,image/webp,image/gif'

/**
 * 아바타 바꾸기 — My Info 의 AVATAR 행에서 연다.
 *
 * 흐름: 파일 선택 → 정사각 크롭 → (브라우저 사전 검사) → 업로드.
 * 크롭 결과는 **실제로 잘린 캔버스**를 보낸다. 관리자 쪽 업로더는 크롭 UI 를 보여주고
 * 원본을 보내고 있었다(캔버스를 뽑는 줄이 주석 처리돼 있다) — 같은 실수를 하지 않는다.
 * 서버가 어차피 512 정사각으로 다시 자르지만, 어디를 자를지는 사용자가 정해야 한다.
 */
export const AvatarUploadModal = (): JSX.Element | null => {
	const reduxDispatch = useAppDispatch()

	const customer: CustomerType = useAppSelector(state => state.customerAuth.customer)
	const isOpen: boolean = useAppSelector(
		state => state.customerAuth.showProfileImageModal,
	)
	const isUploading: boolean = useAppSelector(
		state => state.customerAuth.isUploadingProfileImage,
	)
	const serverError: string = useAppSelector(
		state => state.customerAuth.profileImageErrorMessage,
	)

	const cropperRef = React.useRef<ReactCropperElement>(null)
	const fileInputRef = React.useRef<HTMLInputElement>(null)

	const [source, setSource] = React.useState<string>('')
	const [localError, setLocalError] = React.useState<string>('')
	const [isChecking, setIsChecking] = React.useState<boolean>(false)

	React.useEffect(() => {
		if (!isOpen) {
			setSource('')
			setLocalError('')
			setIsChecking(false)
		}
	}, [isOpen])

	const onClose = () => {
		if (isUploading || isChecking) {
			return
		}
		reduxDispatch(customerAuthActions.SHOW_PROFILE_IMAGE_MODAL(false))
		reduxDispatch(customerAuthActions.PROFILE_IMAGE_ERROR_MESSAGE(''))
	}

	const onFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0]
		e.target.value = ''

		if (file === undefined) {
			return
		}

		setLocalError('')
		reduxDispatch(customerAuthActions.PROFILE_IMAGE_ERROR_MESSAGE(''))

		setIsChecking(true)
		checkPickedFile(file)
			.then((checkResult: ImageCheckResult) => {
				setIsChecking(false)

				if (!checkResult.ok) {
					setLocalError(checkResult.message)
					return
				}

				const reader = new FileReader()
				reader.addEventListener('load', () => setSource(reader.result?.toString() ?? ''))
				reader.readAsDataURL(file)
			})
			.catch(() => {
				// 검사 자체가 실패하면 막지 않는다 — 서버가 판정한다
				setIsChecking(false)
				const reader = new FileReader()
				reader.addEventListener('load', () => setSource(reader.result?.toString() ?? ''))
				reader.readAsDataURL(file)
			})
	}

	const getCroppedFile = async (): Promise<File | null> => {
		const cropper = cropperRef.current?.cropper

		if (cropper === undefined) {
			return null
		}

		const canvas = cropper.getCroppedCanvas({
			width: 512,
			height: 512,
			imageSmoothingQuality: 'high',
		})

		const blob: Blob | null = await new Promise(resolve =>
			canvas.toBlob(resolve, 'image/png'),
		)

		if (blob === null) {
			return null
		}

		return new File([blob], 'avatar.png', { type: 'image/png' })
	}

	const onSave = () => {
		setLocalError('')

		getCroppedFile()
			.then(file => {
				if (file === null) {
					setLocalError('The image could not be prepared. Please choose it again.')
					return
				}

				reduxDispatch(customerAuthActions.UPLOAD_PROFILE_IMAGE({ file: file }))
			})
			.catch(() => {
				setLocalError('The image could not be prepared. Please choose it again.')
			})
	}

	const onRemove = () => {
		reduxDispatch(customerAuthActions.DELETE_PROFILE_IMAGE(true))
	}

	if (!isOpen) {
		return null
	}

	const errorMessage: string = localError !== '' ? localError : serverError
	const isBusy: boolean = isUploading || isChecking

	return (
		<View css={style} className="mnAvatarModalScrim" onClick={onClose}>
			<View className="mnAvatarModal" onClick={e => e.stopPropagation()}>
				<View className="mnAvatarModalHead">
					<span className="mnAvatarModalTitle">Profile picture</span>
					<button
						type="button"
						className="mnAvatarModalClose"
						onClick={onClose}
						aria-label="Close"
						disabled={isBusy}
					>
						✕
					</button>
				</View>

				{source === '' ? (
					<View className="mnAvatarPickView">
						<Avatar
							picture={customer.profileImageUrl}
							name={customer.name}
							color={customer.profileColor === '' ? mono.ink : customer.profileColor}
							size={128}
							extpandingDisable={true}
						/>
						<p className="mnAvatarHint">
							A square image works best. JPEG, PNG, WebP or GIF, up to 8MB.
						</p>
					</View>
				) : (
					<View className="mnAvatarCropView">
						<Cropper
							ref={cropperRef}
							src={source}
							style={{ height: 320, width: '100%' }}
							aspectRatio={1}
							viewMode={1}
							autoCropArea={1}
							background={false}
							responsive={true}
							checkOrientation={false}
							guides={true}
						/>
					</View>
				)}

				{errorMessage !== '' && <span className="mnAvatarError">{errorMessage}</span>}

				<input
					ref={fileInputRef}
					type="file"
					accept={ACCEPT}
					onChange={onFileChange}
					hidden
				/>

				<View className="mnAvatarModalActions">
					{source === '' ? (
						<>
							<button
								type="button"
								className="mnCta"
								onClick={() => fileInputRef.current?.click()}
								disabled={isBusy}
							>
								Choose image
							</button>
							{customer.profileImageUrl !== '' && (
								<button
									type="button"
									className="mnTextLink"
									onClick={onRemove}
									disabled={isBusy}
								>
									Remove picture
								</button>
							)}
						</>
					) : (
						<>
							<button type="button" className="mnCta" onClick={onSave} disabled={isBusy}>
								{isChecking ? 'Checking…' : isUploading ? 'Saving…' : 'Save'}
							</button>
							<button
								type="button"
								className="mnTextLink"
								onClick={() => setSource('')}
								disabled={isBusy}
							>
								Choose another
							</button>
						</>
					)}
				</View>
			</View>
		</View>
	)
}

const style = css({
	position: 'fixed',
	inset: 0,
	zIndex: 1300,
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	padding: 20,
	background: 'rgba(22, 24, 27, 0.7)',
	fontFamily: monoFont.sans,

	'.mnAvatarModal': {
		width: '100%',
		maxWidth: 420,
		background: mono.surface,
		border: `1px solid ${mono.line}`,
		padding: 28,
		display: 'flex',
		flexDirection: 'column',
		gap: 24,
	},

	'.mnAvatarModalHead': {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',

		'.mnAvatarModalTitle': {
			fontSize: 12,
			letterSpacing: '.2em',
			textTransform: 'uppercase',
			color: mono.faint,
			fontWeight: 500,
		},
		'.mnAvatarModalClose': {
			border: 0,
			background: 'transparent',
			color: mono.dim,
			fontSize: 16,
			cursor: 'pointer',
			padding: 0,
			lineHeight: 1,
			'&:hover': { color: mono.ink },
		},
	},

	'.mnAvatarPickView': {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		gap: 20,

		'.mnAvatarHint': {
			fontSize: 13,
			lineHeight: 1.7,
			fontWeight: 300,
			color: mono.dim,
			textAlign: 'center',
			margin: 0,
		},
	},

	'.mnAvatarCropView': {
		width: '100%',
		background: mono.bg,
		border: `1px solid ${mono.line}`,
	},

	'.mnAvatarError': {
		fontSize: 13,
		lineHeight: 1.6,
		color: mono.error,
	},

	'.mnAvatarModalActions': {
		display: 'flex',
		alignItems: 'center',
		gap: 24,
		flexWrap: 'wrap',
	},

	'.mnCta': {
		border: 0,
		background: mono.ctaBg,
		color: '#FFFFFF',
		fontFamily: monoFont.sans,
		fontSize: 13,
		fontWeight: 500,
		padding: '14px 28px',
		cursor: 'pointer',
		transition: 'background-color 200ms ease-out',
		'&:hover': { background: mono.ink },
		'&:disabled': { background: mono.lineSoft, cursor: 'default' },
	},

	'.mnTextLink': {
		border: 0,
		background: 'transparent',
		fontFamily: monoFont.sans,
		fontSize: 13,
		fontWeight: 500,
		color: mono.dim,
		cursor: 'pointer',
		padding: 0,
		transition: 'color 200ms ease-out',
		'&:hover': { color: mono.ink, textDecoration: 'underline' },
		'&:disabled': { color: mono.lineSoft, cursor: 'default' },
	},

	'@media (prefers-reduced-motion: reduce)': {
		'.mnCta, .mnTextLink': { transition: 'none' },
	},
})
